// The sound: on, at half volume, the band and the sound effects, unless the listener says
// otherwise. The switches live under the gear in the header (the band, the sound effects — the
// mute — the volume and a level per part), and again on /settings. The pieces live in sound/: engine (context, samples,
// primitives), cues (the short sounds), music (the door to the band), theory + voices + score
// (the music), ambience (whether the music is heard), prefs.
import { getMix, getVolume, isOn, isUnlocked, resume, setMix, setOn, setVolume } from "./sound/engine.ts";
import { getAmbience, setAmbience, startBed } from "./sound/ambience.ts";
import { glass, pop, tap, warmFall, slam } from "./sound/cues.ts";
import type { Part } from "./sound/prefs.ts";
import { onApp } from "./env.ts";

export { tap, hyoshigi, taiko, opening, bell, shimmer } from "./sound/cues.ts";

/** every sound effects' switch on the page in step with the sound: one listener for the page's life, however many times
 * a soft navigation binds a new <main> (one per switch, each kept alive by the document, was a leak per visit to /settings) */
let fxSynced = false;
const syncFx = () => {
  document.querySelectorAll("[data-fx]").forEach((el) => {
    (el as HTMLInputElement).checked = isOn();
  });
};

const setSound = (v: boolean) => {
  setOn(v);
  if (v) tap(0.5);
};

/** the sound's controls, under the gear and on the settings page: the band's switch, the sound effects' switch (the mute), the volume and the mixer.
 * `root` scopes the binding, so a soft navigation can bind what the new <main> brought. */
export function initSound(root: ParentNode = document) {
  // the band's switch, under the gear and on the settings page: thrown, the band is fetched and joins the bout
  root.querySelectorAll("[data-band]").forEach((el) => {
    const c = el as HTMLInputElement;
    c.checked = getAmbience() === "shamisen";
    c.addEventListener("change", () => {
      setAmbience(c.checked ? "shamisen" : "off");
      if (c.checked && isOn() && !isUnlocked()) resume();
      document.querySelectorAll("[data-band]").forEach((o) => {
        (o as HTMLInputElement).checked = c.checked;
      }); // the mixer's and the gear's, in step
    });
  });
  // the sound effects' switch, under the gear and on the settings page: the sound on or off, every twin in step
  if (!fxSynced) {
    fxSynced = true;
    document.addEventListener("basho:sound", syncFx);
  }
  root.querySelectorAll("[data-fx]").forEach((el) => {
    const c = el as HTMLInputElement;
    c.checked = isOn();
    c.addEventListener("change", () => {
      if (c.checked && !isUnlocked()) resume();
      setSound(c.checked);
      startBed();
      document.dispatchEvent(new window.Event("basho:sound"));
    });
  });
  root.querySelectorAll("[data-volume]").forEach((el) => {
    const r = el as HTMLInputElement;
    r.value = String(Math.round(getVolume() * 100));
    r.addEventListener("input", () => setVolume(+r.value / 100));
  });
  // the mixer, on the settings page and in the header's popover: a level per part
  root.querySelectorAll("[data-mix]").forEach((el) => {
    const r = el as HTMLInputElement;
    const part = r.dataset.mix as Part;
    r.value = String(Math.round(getMix()[part] * 100));
    r.addEventListener("input", () => setMix(part, +r.value / 100));
  });
  // the fall, from the stage (clouds.ts): a pop as the body comes apart, glass for every touch of its pieces after
  if (root === document) {
    // the fall's sounds; a cue may carry its own level (the error page's crush is a little under the ring's)
    // the death sound, a little quieter in the app (Sam, 2026-10-18: "the death sound is a little loud", on the Steam app)
    const deathLevel = onApp() ? 0.75 : 1;
    document.addEventListener("basho:pop", (e) =>
      pop(((e as CustomEvent<{ gain?: number } | null>).detail?.gain ?? 1) * deathLevel),
    );
    document.addEventListener("basho:glass", (e) => {
      const d = (e as CustomEvent<{ n: number; gain?: number }>).detail;
      glass(d.n, d.gain ?? 1);
    });
    // the tachiai's slam and the gyoji's call (two taps) at the verdict: both fired on the bout element without bubbling,
    // so caught on the way down (Sam, 2026-10-18: these three small sounds, and no more)
    document.addEventListener("basho:clash", () => slam(), true);
    document.addEventListener(
      "basho:decided",
      () => {
        tap();
        setTimeout(() => tap(), 90);
      },
      true,
    );
    if (isOn()) warmFall(); // the fall's sounds baked now, not in the frame the body breaks
  }
  if (root === document && isOn() && getAmbience() !== "off") startBed(); // resumes on the first gesture if the browser holds it
}
